import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface DeviceOrganizationInfo {
  organizationId: string | null;
  organizationName: string | null;
}

/**
 * Resolves the organisation a device is assigned to (name + id) via its most
 * recent device assignment and the assigned user's role. Read-only and
 * fail-safe: any error or missing assignment resolves to nulls so the UI
 * can fall back to "Unassigned".
 */
export const useDeviceOrganizationName = (deviceId?: string) => {
  return useQuery({
    queryKey: ["device-organization-name", deviceId],
    queryFn: async (): Promise<DeviceOrganizationInfo> => {
      if (!deviceId) return { organizationId: null, organizationName: null };

      const empty = { organizationId: null, organizationName: null };
      try {
        let orgId: string | null = null;

        const { data: device } = await supabase
          .from("device_details")
          .select("organization_id")
          .eq("device_id", deviceId)
          .maybeSingle();
        orgId = (device as any)?.organization_id ?? null;

        if (!orgId) {
          const { data: assignment } = await supabase
            .from("device_assignments")
            .select("user_id, assigned_at")
            .eq("device_id", deviceId)
            .order("assigned_at", { ascending: false })
            .limit(1)
            .maybeSingle();

          if (assignment?.user_id) {
            const { data: role } = await supabase
              .from("user_roles")
              .select("organization_id")
              .eq("user_id", assignment.user_id)
              .not("organization_id", "is", null)
              .limit(1)
              .maybeSingle();
            orgId = role?.organization_id ?? null;
          }
        }

        if (!orgId) return empty;

        const { data: org } = await supabase
          .from("organizations")
          .select("name")
          .eq("id", orgId)
          .maybeSingle();

        return { organizationId: orgId, organizationName: org?.name ?? null };
      } catch (error) {
        console.error("Error fetching device organization:", error);
        return empty;
      }
    },
    enabled: !!deviceId,
    staleTime: 5 * 60 * 1000,
  });
};
